import { useState } from "react";
import emailjs from "@emailjs/browser";

const SERVICES = ["Aluminium", "Cladding", "Partitions", "Glass", "Other"];

function validate(fields, picked) {
  const errs = {};
  if (!fields.name.trim()) errs.name = "// name is required";
  if (!fields.phone.trim() || !/^[+\d\s().-]{7,}$/.test(fields.phone)) errs.phone = "// valid phone required";
  if (fields.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) errs.email = "// valid email required";
  if (picked.size === 0) errs.services = "// select at least one service";
  return errs;
}

export default function Contact() {
  const [fields, setFields] = useState({ name: "", phone: "", email: "", message: "", type: "" });
  const [picked, setPicked] = useState(new Set());
  const [errs, setErrs] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState("");

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));
  const clearErr = k => setErrs(e => { const n = { ...e }; delete n[k]; return n; });

  const toggleSvc = (s) => {
    setPicked(p => {
      const n = new Set(p);
      n.has(s) ? n.delete(s) : n.add(s);
      return n;
    });
    clearErr("services");
  };

  const reset = () => {
    setFields({ name: "", phone: "", email: "", message: "", type: "" });
    setPicked(new Set());
    setErrs({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate(fields, picked);
    if (Object.keys(v).length) { setErrs(v); return; }

    setSending(true);
    setSendErr("");
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name:    fields.name,
          from_phone:   fields.phone,
          from_email:   fields.email || "—",
          services:     Array.from(picked).join(", "),
          project_type: fields.type || "—",
          message:      fields.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); reset(); }, 4000);
    } catch {
      setSendErr("// something went wrong — please call us directly.");
    } finally {
      setSending(false);
    }
  };

  const field = (k, label, req, inputProps) => (
    <div style={{ marginBottom: "24px" }}>
      <label style={{ display: "block", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "8px" }}>
        {label} {req && <span style={{ color: "var(--blue)" }}>*</span>}
      </label>
      <input
        {...inputProps}
        value={fields[k]}
        onChange={e => { set(k, e.target.value); clearErr(k); }}
        className={`inquiry-field-input${errs[k] ? " has-error" : ""}`}
      />
      {errs[k] && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#d00", letterSpacing: "0.08em", marginTop: "6px" }}>{errs[k]}</div>}
    </div>
  );

  return (
    <section id="contact" style={{ padding: "120px 0", borderBottom: "1px solid var(--ink)" }}>
      <div style={{ maxWidth: "1440px", margin: "0 auto", padding: "0 32px" }}>
        <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: "80px" }}>

          {/* Left — contact info */}
          <div>
            <div className="section-tag">// Contact — No. 05</div>
            <h2 style={{ fontSize: "clamp(40px, 4.5vw, 64px)", lineHeight: 0.98, letterSpacing: "-0.025em", fontWeight: 500, marginTop: "12px" }}>
              Tell us about<br />your project.<br />We'll{" "}
              <span style={{ fontWeight: 400, color: "var(--blue)" }}>reach out</span>.
            </h2>
            <p style={{ fontSize: "17px", lineHeight: 1.5, color: "#222", marginTop: "28px", maxWidth: "40ch" }}>
              Typical response within one business day.
            </p>

            <div style={{ marginTop: "40px", borderTop: "1px solid var(--ink)" }}>
              {/* People */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: "1px solid var(--line)" }}>
                {[
                  { role: "// Founder", name: "Upasiri Hatharasinghe", phone: "+94777754050", display: "+94 77 775 4050", photo: "CEO.jpg" },
                  { role: "// Project manager", name: "Akila Hatharasinghe", phone: "+94771648604", display: "+94 77 164 8604", photo: "PM.jpg" },
                  { role: "// Engineer", name: "Prasad Wanniarachchi", phone: "+94775052564", display: "+94 77 505 2564", photo: "prasad.jpg" },
                ].map((p, i) => (
                  <div key={p.name} style={{ padding: "20px 20px 22px 0", display: "flex", flexDirection: "column", gap: "4px", borderLeft: i > 0 ? "1px solid var(--line)" : "none", paddingLeft: i > 0 ? "20px" : 0 }}>
                    <img
                      src={`${process.env.PUBLIC_URL}/${p.photo}`}
                      alt={p.name}
                      style={{ width: "72px", height: "72px", objectFit: "cover", objectPosition: "center top", borderRadius: "50%", border: "1px solid var(--blue)", marginBottom: "10px" }}
                    />
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--blue)", marginBottom: "6px" }}>{p.role}</div>
                    <div style={{ fontSize: "16px", fontWeight: 500, lineHeight: 1.25 }}>{p.name}</div>
                    <a className="ph-line" href={`tel:${p.phone}`} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", letterSpacing: "0.02em", marginTop: "6px" }}>{p.display}</a>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.1em", color: "var(--muted)", textTransform: "uppercase", marginTop: "2px" }}>WhatsApp available</div>
                  </div>
                ))}
              </div>

              {/* Address */}
              <div style={{ padding: "18px 0", borderBottom: "1px solid var(--line)", display: "grid", gridTemplateColumns: "90px 1fr", gap: "24px", alignItems: "start" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", paddingTop: "3px" }}>Address</div>
                <div style={{ fontSize: "15px", lineHeight: 1.5, fontWeight: 500 }}>125 Wattegedara Road<br />Maharagama 10280<br />Sri Lanka</div>
              </div>

              {/* Hours */}
              <div style={{ padding: "18px 0", borderBottom: "1px solid var(--line)", display: "grid", gridTemplateColumns: "90px 1fr", gap: "24px", alignItems: "start" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", paddingTop: "3px" }}>Hours</div>
                <div style={{ fontSize: "15px", lineHeight: 1.5, fontWeight: 500 }}>
                  Mon — Sat<br />
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px", color: "var(--muted)", fontWeight: 400 }}>08:00 — 19:00 IST</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div>
            <form
              onSubmit={handleSubmit}
              noValidate
              style={{ border: "1px solid var(--ink)", background: "#fff", padding: "32px", position: "relative" }}
            >
              {/* Form label tag */}
              <div style={{ position: "absolute", top: "-10px", left: "24px", background: "#fff", padding: "0 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.14em", color: "var(--blue)" }}>
                // 01
              </div>

              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "24px" }}>
                Project inquiry form
              </div>

              {/* Name + Phone */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                {field("name", "Name", true, { type: "text", placeholder: "Your full name" })}
                {field("phone", "Phone", true, { type: "tel", placeholder: "+94 ___ _______" })}
              </div>

              {field("email", "Email", false, { type: "email", placeholder: "you@company.lk" })}

              {/* Services */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "8px" }}>
                  Services needed <span style={{ color: "var(--blue)" }}>*</span>
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                  {SERVICES.map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`svc-chip${picked.has(s) ? " selected" : ""}`}
                      onClick={() => toggleSvc(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {errs.services && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#d00", letterSpacing: "0.08em", marginTop: "6px" }}>{errs.services}</div>}
              </div>

              {/* Project type */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "8px" }}>Project type</label>
                <select value={fields.type} onChange={e => set("type", e.target.value)} className="inquiry-field-input">
                  <option value="">Choose one…</option>
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Government</option>
                  <option>Institutional</option>
                </select>
              </div>

              {/* Message */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "8px" }}>
                  Describe the project
                </label>
                <textarea
                  value={fields.message}
                  onChange={e => { set("message", e.target.value); clearErr("message"); }}
                  placeholder="Location, floors, approximate area, timeline, any reference drawings…"
                  className={`inquiry-textarea${errs.message ? " has-error" : ""}`}
                />
                {errs.message && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#d00", letterSpacing: "0.08em", marginTop: "6px" }}>{errs.message}</div>}
              </div>

              {/* Submit row */}
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "32px", paddingTop: "24px", borderTop: "1px solid var(--line)" }}>
                <button type="submit" className="btn" disabled={sending} style={{ opacity: sending ? 0.6 : 1 }}>
                  {sending ? "Sending…" : submitted ? "✓ Sent" : "Send inquiry →"}
                </button>
              </div>
              {sendErr && (
                <div style={{ marginTop: "12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#d00", letterSpacing: "0.08em" }}>{sendErr}</div>
              )}

              {submitted && (
                <div style={{ marginTop: "16px", padding: "16px", background: "var(--blue)", color: "#fff", fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", letterSpacing: "0.08em" }}>
                  ✓ Inquiry received. We'll call you back within one business day.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
